<template>
  <div class="calendar-item-section" :class="{ 'calendar-item-section--is-pc': isPc }">
    <div class="calendar-item-section__date-container">
      <p class="calendar-item-section__date">{{ displayEventDate(calendarItem.eventDate) }}</p>
      <p class="calendar-item-section__date-day">
        {{ displayEventDayOfWeek(calendarItem.eventDate) }}
      </p>
    </div>
    <div class="calendar-item-section__main">
      <p class="calendar-item-section__name">
        <a
          v-if="externalLinks"
          :href="externalLinks"
          class="calendar-item-section__name-link"
          target="_blank"
        >
          <span v-html="disPlayEventName(calendarItem.eventName)" />
        </a>
        <span
          v-else
          class="calendar-item-section__name-label"
          v-html="disPlayEventName(calendarItem.eventName)"
        />
      </p>
      <ul class="calendar-item-section__links">
        <li class="calendar-item-section__links-item">
          <a
            v-if="calendarItem.placeLink"
            :href="calendarItem.placeLink"
            class="calendar-item-section__links-item-link"
            target="_blank"
            >{{ calendarItem.placeName }}</a
          >
          <span
            v-else
            class="calendar-item-section__links-item-link calendar-item-section__links-item-link--no-link"
            v-html="disPlayEventName(calendarItem.placeName)"
          />
        </li>
        <li class="calendar-item-section__links-item">
          <a
            v-if="calendarItem.placeLink"
            :href="calendarItem.categoryLink"
            class="calendar-item-section__links-item-link"
            target="_blank"
            >{{ calendarItem.categoryName }}</a
          >
          <span
            v-else
            class="calendar-item-section__links-item-link calendar-item-section__links-item-link--no-link"
            v-html="disPlayEventName(calendarItem.categoryName)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import sanitizeHtml from 'sanitize-html'
import CalendarDataDocument from '@/store/stores/collectionModule/documents/calendar/CalendarDataDocument'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'
import DeviceInfo from '@/util/DeviceInfo'

export default defineComponent({
  name: 'CalendarItemSection',
  methods: { sanitizeHtml },
  props: {
    calendarItem: {
      type: Object as PropType<CalendarDataDocument>,
      required: true,
    },
  },
  setup() {
    const { getDisplayDateJa } = useDisplayDependingOnLang()

    /** PCブラウザかどうか */
    const isPc = DeviceInfo.isForPC()
    // TODO: 将来的な対応として、イベント名のリンク先をManagementから登録するファイルで指定できるようにする
    const externalLinks = ''

    /**
     * カレンダーに表示するイベント日付
     */
    const displayEventDate = (eventDate: number | null) => {
      if (!eventDate) {
        return ''
      }

      return getDisplayDateJa(eventDate, 'M/D', 'MMM.D')
    }

    /**
     * カレンダーに表示する曜日
     */
    const displayEventDayOfWeek = (eventDate: number | null) => {
      if (!eventDate) {
        return ''
      }

      return getDisplayDateJa(eventDate, 'ddd', 'ddd')
    }

    /**
     * イベント名
     */
    const disPlayEventName = (eventName: string | null) =>
      // 改行コードを <br>に変換し、サニタイジングする
      eventName ? sanitizeHtml(eventName.replace(/(\r\n|\r|\n)/g, '<br>')) : ''

    return {
      isPc,
      externalLinks,
      displayEventDate,
      displayEventDayOfWeek,
      disPlayEventName,
    }
  },
})
</script>

<style scoped lang="scss">
.calendar-item-section {
  display: flex;
  gap: 5px;
  align-items: center;
  padding: 12px 12px 12px 5px;
  border-bottom: 1px solid $black10;

  &--is-pc {
    .calendar-item-section__name-link {
      padding-right: 24px;
      font-size: 18px;
      background-size: 20px;
    }

    .calendar-item-section__name-label {
      font-size: 18px;
    }
  }

  &__date {
    font-size: 18px;
    font-weight: 500;
    color: $black;
    letter-spacing: 0.02em;
    line-height: 1.2;

    &-container {
      display: flex;
      flex-shrink: 0;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 80px;
      height: 40px;
    }

    &-day {
      font-size: 12px;
      font-weight: 500;
      color: $gray75;
      letter-spacing: 0.04em;
      line-height: 1.2;
    }
  }

  &__name {
    &-link {
      padding-right: 18px;
      font-size: 14px;
      font-weight: 700;
      color: $blue-light;
      letter-spacing: 0.02em;
      line-height: 1.8;
      word-break: break-all;
      background-image: url('@/assets/img/icon/icon_external_link_lightblue.svg');
      background-repeat: no-repeat;
      background-position: right bottom 3px;
      background-size: 16px;
      border-bottom: 1px solid $blue-light;
    }

    &-label {
      font-size: 14px;
      font-weight: 700;
      color: $black;
      line-height: 1.43;
      letter-spacing: 0.02em;
    }
  }

  &__links {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 4px;

    &-item {
      &:first-child {
        position: relative;

        &::before {
          position: absolute;
          top: 5px;
          right: -8px;
          content: '/';
          font-size: 10px;
          font-weight: 500;
          color: $black50;
        }
      }

      &-link {
        padding-right: 14px;
        font-size: 10px;
        font-weight: 500;
        color: $black70;
        letter-spacing: 0.04em;
        white-space: nowrap;
        background-image: url('@/assets/img/icon/icon_external_link_gray75.svg');
        background-repeat: no-repeat;
        background-position: right bottom 2px;
        background-size: 12px;
        border-bottom: 1px solid $black70;

        &--no-link {
          text-decoration: none;
        }

        &-icon {
          display: inline-block;
          width: 12px;
          height: 12px;
          color: $black50;
        }
      }
    }
  }
}
</style>
